/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface EventItem {
  id: string;
  eventName: string;
  date: string;
  description: string;
  imageUrl: string;
}

interface EventsContent {
  title: string;
  description: string;
  events: EventItem[];
}

export default function GalleryEvents() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<EventsContent>({
    title: 'Temple Events',
    description: 'Gallery of temple events',
    events: [
      { id: '1', eventName: 'Nadapanthal Inauguration Day', date: '2024-09-15', description: 'Grand inauguration ceremony of the Nadapanthal with special poojas and community gathering.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_1.jpeg` },
      { id: '2', eventName: 'Nadapanthal Pooja', date: '2024-03-08', description: 'Special pooja ceremony conducted at the Nadapanthal with rituals and devotional programs.', imageUrl: `${process.env.PUBLIC_URL}/assets/img_2.jpeg` },
    ]
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'gallery_content', 'events'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as EventsContent);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading events:', error);
      setLoading(false);
    }
  }, []);

  const events = content.events;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Events Gallery — Kottayil Temple" description="Photos and memories from events, festivals, and celebrations at Kottayil Temple (Kotta-Kovilakam Kshethram), Muchukunnu." path="/gallery/events" keywords="Kottayil temple events, celebration photos, event images, ulsavam photos, Muchukunnu temple events, കൊട്ടയിൽ ഉത്സവ ചിത്രങ്ങൾ" />
      <PageBanner title="Temple Events" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{ borderRadius: '24px', overflow: 'hidden', mb: 3.5, boxShadow: '0 20px 60px rgba(245,158,11,0.15)' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #78350f 0%, #f59e0b 55%, #fde68a 100%)',
                px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 5, sm: 6 },
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  }}>
                    <CelebrationIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontSize: { xs: '1.8rem', sm: '2.4rem' }, mb: 1.5, lineHeight: 1.1 }}>
                    Temple Events
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.7, maxWidth: 500, mb: 2.5 }}>
                    Relive the joyous celebrations, sacred ceremonies, and community gatherings at our temple.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<CelebrationIcon sx={{ fontSize: 16, color: '#f59e0b !important' }} />} label="Events" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#92400e', fontWeight: 700, fontSize: '0.7rem', height: 28, borderRadius: '14px' }} />
                    <Chip icon={<EventAvailableIcon sx={{ fontSize: 16, color: '#fff !important' }} />} label={`${events.length} Celebrations`} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 28, borderRadius: '14px', backdropFilter: 'blur(10px)' }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* EVENT CARDS */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {events.map((event, index) => {
                const eGradients: [string, string][] = [
                  ['#f59e0b', '#d97706'], ['#8b5cf6', '#7c3aed'],
                  ['#10b981', '#059669'], ['#ec4899', '#db2777'],
                  ['#3b82f6', '#2563eb'], ['#ef4444', '#dc2626'],
                ];
                const gr = eGradients[index % eGradients.length];
                return (
                  <Box key={index} sx={{
                    bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 20px 50px rgba(0,0,0,0.1)' },
                  }}>
                    {/* Gradient header */}
                    <Box sx={{
                      background: `linear-gradient(135deg, ${gr[0]}, ${gr[1]})`,
                      px: { xs: 2.5, sm: 3 }, py: 1.8,
                      display: 'flex', alignItems: 'center', gap: 1.5,
                    }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '10px',
                        background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                        border: '1.5px solid rgba(255,255,255,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CelebrationIcon sx={{ fontSize: 18, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff', flex: 1 }}>
                        {event.eventName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.2, py: 0.3, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}>
                        <CalendarMonthIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.9)' }} />
                        <Typography sx={{ fontSize: '0.62rem', color: '#fff', fontWeight: 700 }}>
                          {new Date(event.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </Typography>
                      </Box>
                    </Box>
                    <Grid container>
                      <Grid item xs={12} md={5}>
                        <Box sx={{ position: 'relative', height: { xs: 200, md: '100%' }, minHeight: { md: 200 } }}>
                          <Box component="img" src={event.imageUrl} alt={event.eventName}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Box sx={{ p: { xs: 2.5, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                          <Typography sx={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.8, mb: 2 }}>
                            {event.description}
                          </Typography>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: '10px', bgcolor: `${gr[0]}10`, border: `1px solid ${gr[0]}20` }}>
                            <CalendarMonthIcon sx={{ fontSize: 14, color: gr[0] }} />
                            <Typography sx={{ fontSize: '0.72rem', color: gr[0], fontWeight: 700 }}>
                              {new Date(event.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })}
            </Box>

            {/* FOOTER */}
            <Box sx={{
              mt: 3.5, borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              boxShadow: '0 8px 32px rgba(15,23,42,0.3)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3, position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>Celebrating Sacred Traditions</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, maxWidth: 500, mx: 'auto' }}>
                  Join us in our upcoming temple events and be part of our vibrant community celebrations.
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="gallery" activePath="/gallery/events" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
